/**
 * Card number input with real-time validation and card type detection
 */

import React, { useState, useEffect } from 'react';
import { TextField, InputAdornment, Box, Typography } from '@mui/material';
import { formatCardNumber } from '../services/formatters';
import { detectCardType, getCardTypeName } from '../services/cardTypeDetector';
import { CardType } from '../types/CardData';
import "./CardForm.css";

export interface CardNumberInputProps {
  value: string;
  onChange: (value: string, cardType: CardType) => void;
  onBlur?: () => void;
  error?: string;
  disabled?: boolean;
}

export const CardNumberInput: React.FC<CardNumberInputProps> = ({
  value,
  onChange,
  onBlur,
  error,
  disabled = false,
}) => {
  const [cardType, setCardType] = useState<CardType>('unknown');
  

  // Update card type when value changes
  useEffect(() => {
    const type = detectCardType(value);
    setCardType(type);
  }, [value]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = event.target.value;
    const formatted = formatCardNumber(rawValue);

      const type = detectCardType(formatted);
      onChange(formatted, type);
    
  };

  const handleBlur = () => {
    onBlur?.();
  };

  // Show error from parent (form submission) immediately, or show live validation only if touched
  const displayError = error;

  return (
    <Box>
      <div className="field-container align-items-center">
        <div className="label">
          <label className="p-2" data-testid="CardNumber" htmlFor="cardNumber">
            Card Number
          </label>
          <span className="text-danger" data-testid="CardNumber-Astrict-icon">
            *
          </span>
        </div>
        <TextField
          id="cardNumber"
          className="textfield"
          fullWidth
          // label="Card Number"
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          error={!!displayError}
          helperText={displayError || ' '}
          disabled={disabled}
          autoComplete="cc-number"
          inputMode="numeric"
          placeholder="1234 5678 9012 3456"
          required
          InputProps={{
            endAdornment: (
              cardType !== 'unknown' && (
                <InputAdornment position="end">
                  <Typography
                    component="span"
                    sx={{
                      fontSize: '1.25rem',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <span
                      className={`card-icon ${cardType}`}
                      aria-label={getCardTypeName(cardType)}
                    />
                  </Typography>
                </InputAdornment>
              )
            ),
          }}
          inputProps={{
            'aria-label': 'Card number',
            'aria-required': 'true',
            'aria-invalid': !!displayError,
            'aria-describedby': displayError ? 'card-number-error' : undefined,
           
          }}
        />
      </div>
    </Box>
  );
};

export default CardNumberInput;
